import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Trash2, X } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    // Focus confirm button so Enter works immediately
    setTimeout(() => confirmRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onConfirm, onCancel]);

  if (!isOpen) return null;

  const portalTarget = document.getElementById('modal-root') || document.body;

  const iconBg =
    variant === 'danger'  ? 'bg-rose-500/10'   :
    variant === 'warning' ? 'bg-amber-500/10'  : 'bg-slate-100 dark:bg-white/5';

  const icon =
    variant === 'danger'  ? <Trash2 size={22} className="text-rose-500" />         :
    variant === 'warning' ? <AlertTriangle size={22} className="text-amber-500" /> : null;

  const confirmCls =
    variant === 'danger'  ? 'bg-rose-500 hover:bg-rose-600 text-white' :
    variant === 'warning' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'btn-primary';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />

      <div
        className="relative z-10 w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'confirmEntrance 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
      >
        {/* Close */}
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
        >
          <X size={16} />
        </button>

        <div className="p-6 space-y-4">
          {icon && (
            <div className={`w-12 h-12 rounded-2xl ${iconBg} flex items-center justify-center`}>
              {icon}
            </div>
          )}
          <div>
            <h3 className="text-base font-bold text-slate-800 dark:text-white">{title}</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{message}</p>
          </div>
        </div>

        <div className="px-6 pb-6 flex justify-end gap-3">
          <button onClick={onCancel} className="btn-secondary px-4 py-2">
            {cancelLabel}
          </button>
          <button ref={confirmRef} onClick={onConfirm} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${confirmCls}`}>
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes confirmEntrance {
          from { transform: scale(0.92) translateY(8px); opacity: 0; }
          to   { transform: scale(1)    translateY(0);   opacity: 1; }
        }
      `}</style>
    </div>,
    portalTarget
  );
};

export default ConfirmDialog;
